import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { TableMap } from '@tiptap/pm/tables';
import type { EditorView } from '@tiptap/pm/view';

/**
 * TableFit
 * --------
 * Keeps every table inside the editor from growing wider than the writable
 * content column.
 *
 * Why this is needed: Tiptap's column-resizing writes explicit pixel
 * `colwidth` values on the dragged column but leaves the others "auto".
 * prosemirror-tables then renders the `<table>` with an inline
 * `min-width: <sum>px`. Because `min-width` overrides our CSS
 * `max-width: 100%`, a widened column pushes the whole table past the page
 * margins ("outside the editor").
 *
 * The fix: after any change settles, measure the table's available width
 * (its `.tableWrapper`, which is locked to the text column) and, if the
 * column widths sum to more than that, scale every column down
 * proportionally and write the result back to the model. Because all
 * columns then carry an explicit width whose sum equals the available
 * width, prosemirror-tables switches to `width` (not `min-width`) and the
 * table fits exactly — just like Microsoft Word, where widening one column
 * steals space from the others instead of overflowing the page.
 */
const key = new PluginKey('tableFit');
const TOLERANCE_PX = 2;
const MIN_COL_PX = 12;

function getAvailableWidth(tableEl: HTMLElement, root: HTMLElement): number {
  const wrapper = tableEl.closest('.tableWrapper') as HTMLElement | null;
  const width = wrapper?.clientWidth || root.clientWidth || 0;
  return Math.floor(width);
}

/** Read the current per-column widths from the rendered <colgroup>. */
function getDomColWidths(tableEl: HTMLElement): number[] {
  const cols = Array.from(tableEl.querySelectorAll('colgroup > col')) as HTMLElement[];
  return cols.map((col) => {
    const styleWidth = parseFloat(col.style.width);
    if (Number.isFinite(styleWidth) && styleWidth > 0) return styleWidth;
    return col.getBoundingClientRect().width || 0;
  });
}

/** Scale `widths` so they sum to `available`, never below MIN_COL_PX, with
 * any rounding remainder folded into the widest column. */
function scaleToFit(widths: number[], available: number): number[] {
  const total = widths.reduce((sum, w) => sum + w, 0);
  if (total <= 0) return widths;
  const scale = available / total;
  const scaled = widths.map((w) => Math.max(MIN_COL_PX, Math.round(w * scale)));
  const scaledTotal = scaled.reduce((sum, w) => sum + w, 0);
  const diff = available - scaledTotal;
  if (diff !== 0) {
    let widest = 0;
    for (let i = 1; i < scaled.length; i += 1) {
      if (scaled[i] > scaled[widest]) widest = i;
    }
    scaled[widest] = Math.max(MIN_COL_PX, scaled[widest] + diff);
  }
  return scaled;
}

/** Walk every table in the document; clamp any that overflow. Returns true
 * if a transaction was dispatched. */
function clampTables(view: EditorView): boolean {
  const root = view.dom as HTMLElement;
  const tableEls = Array.from(root.querySelectorAll('table')) as HTMLTableElement[];
  if (!tableEls.length) return false;

  const tr = view.state.tr;
  let changed = false;

  for (const tableEl of tableEls) {
    const available = getAvailableWidth(tableEl, root);
    if (available <= 0) continue;

    const domWidths = getDomColWidths(tableEl);
    if (!domWidths.length) continue;

    const total = domWidths.reduce((sum, w) => sum + w, 0);
    if (total <= available + TOLERANCE_PX) continue; // already fits

    const newWidths = scaleToFit(domWidths, available);

    let pos: number;
    try {
      pos = view.posAtDOM(tableEl, 0);
    } catch {
      continue;
    }
    if (pos < 0) continue;

    const $pos = view.state.doc.resolve(pos);
    let depth = $pos.depth;
    while (depth > 0 && $pos.node(depth).type.name !== 'table') depth -= 1;
    if (depth <= 0) continue;

    const tableNode = $pos.node(depth);
    const tableStart = $pos.before(depth) + 1;
    const map = TableMap.get(tableNode);
    if (map.width !== newWidths.length) continue; // colgroup/model mismatch

    const seen = new Set<number>();
    for (let row = 0; row < map.height; row += 1) {
      for (let col = 0; col < map.width; col += 1) {
        const rel = map.map[row * map.width + col];
        if (seen.has(rel)) continue;
        seen.add(rel);

        const cell = tableNode.nodeAt(rel);
        if (!cell) continue;

        const colspan = (cell.attrs.colspan as number) || 1;
        const widthArr: number[] = [];
        for (let i = 0; i < colspan; i += 1) {
          widthArr.push(newWidths[col + i] ?? newWidths[col] ?? MIN_COL_PX);
        }

        const current = cell.attrs.colwidth as number[] | null;
        const unchanged =
          current &&
          current.length === widthArr.length &&
          current.every((value, i) => value === widthArr[i]);
        if (unchanged) continue;

        tr.setNodeMarkup(tableStart + rel, undefined, { ...cell.attrs, colwidth: widthArr });
        changed = true;
      }
    }
  }

  if (changed) {
    view.dispatch(tr.setMeta('addToHistory', false).setMeta(key, true));
    return true;
  }
  return false;
}

export const TableFit = Extension.create({
  name: 'tableFit',
  addProseMirrorPlugins() {
    let raf = 0;
    let pointerDown = false;
    return [
      new Plugin({
        key,
        view(view) {
          const runClamp = () => {
            raf = 0;
            // Never reflow mid-drag; the trailing pointerup pass handles it.
            if (pointerDown) return;
            clampTables(view);
          };
          const schedule = () => {
            if (raf) return;
            raf = window.requestAnimationFrame(runClamp);
          };
          const onPointerDown = () => {
            pointerDown = true;
          };
          const onPointerUp = () => {
            if (!pointerDown) return;
            pointerDown = false;
            schedule();
          };

          window.addEventListener('pointerdown', onPointerDown, true);
          window.addEventListener('pointerup', onPointerUp, true);
          window.addEventListener('pointercancel', onPointerUp, true);
          schedule();

          return {
            update() {
              schedule();
            },
            destroy() {
              if (raf) window.cancelAnimationFrame(raf);
              window.removeEventListener('pointerdown', onPointerDown, true);
              window.removeEventListener('pointerup', onPointerUp, true);
              window.removeEventListener('pointercancel', onPointerUp, true);
            }
          };
        }
      })
    ];
  }
});
